import { Copy, Pencil, Trash2 } from "lucide-react";
import type { OAuthClient } from "@/types";

interface OAuthClientListProps {
  clients: OAuthClient[];
  onToggleEnabled: (client: OAuthClient) => void;
  onEdit: (client: OAuthClient) => void;
  onDelete: (client: OAuthClient) => void;
  onCopy: (value: string) => void;
}

export function OAuthClientList({
  clients,
  onToggleEnabled,
  onEdit,
  onDelete,
  onCopy,
}: Readonly<OAuthClientListProps>) {
  return (
    <div className="space-y-3">
      {clients.map((c) => (
        <div key={c.uuid} className="rounded-lg border border-border-primary bg-surface-secondary p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-content-primary">{c.clientName}</span>
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-surface-primary text-content-tertiary">
                  {c.clientType}
                </span>
                {c.isFirstParty && (
                  <span className="text-[10px] uppercase tracking-wide text-brand-primary">first-party</span>
                )}
                {!c.isEnabled && <span className="text-[10px] uppercase tracking-wide text-red-400">disabled</span>}
              </div>
              <div className="mt-1 text-xs text-content-tertiary font-mono no-ligatures flex items-center gap-1.5">
                {c.clientId}
                <button type="button" onClick={() => onCopy(c.clientId)} className="hover:text-content-primary">
                  <Copy className="h-3 w-3" />
                </button>
              </div>
              <div className="mt-2 text-xs text-content-quaternary break-all">{c.redirectUris.join(", ")}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => onToggleEnabled(c)}
                className="text-xs px-2 py-1 rounded border border-border-primary text-content-tertiary hover:text-content-primary"
              >
                {c.isEnabled ? "Disable" : "Enable"}
              </button>
              <button
                type="button"
                onClick={() => onEdit(c)}
                className="p-1.5 rounded text-content-tertiary hover:text-content-primary hover:bg-surface-primary"
                aria-label="Edit client"
              >
                <Pencil className="h-4 w-4" />
              </button>
              {!c.isFirstParty && (
                <button
                  type="button"
                  onClick={() => onDelete(c)}
                  className="p-1.5 rounded text-red-400 hover:bg-red-500/10"
                  aria-label="Delete client"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
